import { z } from "zod";

// CoachAthleteEdge — directed, double-consent relationship between a
// coach and an athlete. Source of truth + invariants in
// /home/tmow/code/jackedlogs/ROLES_AND_PERMISSIONS.md §Relationships.
//
// State machine:
//   - "invited_by_coach"   : coach sent an invite; athlete hasn't responded.
//                            Coach can revoke. No access yet.
//   - "invited_by_athlete" : athlete sent an invite; coach hasn't responded.
//                            Athlete can revoke. No access yet.
//   - "active"             : both sides consented. Coach has the read-only
//                            slice on the athlete's data (Records / MAXes /
//                            session metadata) per ROLES_AND_PERMISSIONS.md.
//                            Athlete can assign self-coaching via the same
//                            shape (coachSub === athleteSub).
//   - "revoked"            : either side withdrew. Kept for audit; treated
//                            as absent by every permission check.
//
// Activation NEVER happens on the invite write alone. A coach can't grant
// themselves access by spamming invitations; an athlete can't be added to
// a coach's roster without explicit consent.
export const CoachAthleteEdgeStatus = z.enum([
  "invited_by_coach",
  "invited_by_athlete",
  "active",
  "revoked",
]);
export type CoachAthleteEdgeStatus = z.infer<typeof CoachAthleteEdgeStatus>;

// What the athlete has granted this coach BEYOND the read-only slice an
// `active` edge carries on its own.
//
// ⛔ An active edge is NOT a blanket delegation. `active` means exactly
// the read-only slice in ROLES_AND_PERMISSIONS.md. Anything past it —
// writing into the athlete's data, or the special-category health
// metrics — is a SEPARATE capability the athlete grants explicitly, and
// it lives here.
//
// This is the consent scaffold coachReadProjection.ts asks for ("that
// consent scaffold does NOT exist yet — CoachAthleteEdge is a flat
// active/invited/revoked relationship, no per-metric grain"). When the
// per-metric health consent lands it MUST extend this enum rather than
// model a second permission vocabulary: two vocabularies on one
// relationship can drift, and a permission check that disagrees with
// another permission check is the worst failure mode we have.
//
//   - "write:targets" : the coach may set the athlete's weekly Program
//                       targets (CO-7). Reads stay edge-only.
//
// The "coach" on the far side may be a human OR a service — the CO-12 AI
// coach, or a future third party. The invite/onboarding copy must say so
// plainly: this grant is the moment an athlete consents to being written
// to, and consent that wasn't informed isn't consent.
export const CoachGrant = z.enum(["write:targets"]);
export type CoachGrant = z.infer<typeof CoachGrant>;

// DDB layout: the edge is written TWICE on accept — once per partition —
// so both the coach's and the athlete's USER#<sub> queries surface the
// relationship without a cross-partition fan-out. Both rows carry the
// same {coachSub, athleteSub, status, invitedAt, respondedAt} payload;
// they only differ by (pk, sk).
//
//   coach's row    : pk = USER#<coachSub>,   sk = EDGE#<athleteSub>
//   athlete's row  : pk = USER#<athleteSub>, sk = EDGE#<coachSub>
//
// `id` is a single UUID shared across both rows so an audit query can
// correlate the two halves. The partition direction is encoded by the
// (pk, sk) pair; the payload duplication is denormalisation in service
// of read performance at family scale.
//
// Self-coaching (Antenore today): coachSub === athleteSub. Schema does
// not special-case it — the resolver writes one row at pk=USER#<sub>,
// sk=EDGE#<sub> with status='active' on both sides simultaneously.
export const CoachAthleteEdge = z.object({
  id: z.string().min(1),
  coachSub: z.string().min(1).max(128),
  athleteSub: z.string().min(1).max(128),
  status: CoachAthleteEdgeStatus,
  // The email the invitation was addressed to. Stored on the invite row
  // so the receiving side can match against their verified Cognito email
  // at accept time (an invite to alice@x.com can't be accepted by an
  // account holding bob@y.com). Cleared once the edge transitions to
  // active — at that point both sides are identified by sub.
  invitedEmail: z.string().email().max(254).optional(),
  // Free-text note the inviter can attach ("Spring strength block",
  // "Pulling reset 4 weeks"). Capped at 280 to avoid the row growing
  // unbounded. Stripped on revoke.
  inviteNote: z.string().min(1).max(280).optional(),
  // Capabilities the ATHLETE granted this coach on top of `active`. See
  // CoachGrant above for the vocabulary.
  //
  // ⛔ Optional with NO default, on purpose. Every edge that existed
  // before CO-7 was accepted under read-only semantics; a schema change
  // must never hand those edges a write capability their athlete never
  // agreed to. Absent = no grants = read-only, forever, until the
  // athlete adds one. You can ask for consent late — you cannot undo a
  // write made without it.
  //
  // Only the athlete side may add to this list (the coach granting
  // themselves write would defeat the point). Bounded so the row can't
  // grow without limit.
  grants: z.array(CoachGrant).max(8).optional(),
  invitedAt: z.iso.datetime(),
  // Set when status transitions from invited_* → active OR revoked. Null
  // while still pending. ISO so DDB sort comparisons work as expected.
  respondedAt: z.iso.datetime().optional(),
  // ISO of the most recent state mutation. Used for last-seen-by-coach /
  // last-seen-by-athlete decisions in the future invite banner; written
  // on every PUT.
  updatedAt: z.iso.datetime(),
});
export type CoachAthleteEdge = z.infer<typeof CoachAthleteEdge>;

// Client → server payload for creating an invitation. The inviter's sub
// is server-derived from claims; `id`/`status`/`invitedAt`/`updatedAt`
// are server-stamped. The wire shape carries only what the inviter
// actually picks: the peer's identifying handle (sub OR email) and the
// optional note.
export const NewCoachAthleteEdge = z.object({
  // Exactly one of these must be set on a create. `peerSub` is used when
  // the inviter knows the peer's sub directly (admin-curated link, or a
  // re-invite after revoke). `peerEmail` is the common path — the
  // inviter types an email and the server matches it against the
  // Cognito user pool at accept time.
  peerSub: z.string().min(1).max(128).optional(),
  peerEmail: z.string().email().max(254).optional(),
  inviteNote: z.string().min(1).max(280).optional(),
});
export type NewCoachAthleteEdge = z.infer<typeof NewCoachAthleteEdge>;

// Returns true if the edge represents a live coach→athlete authorisation.
// 'revoked' and any 'invited_*' state return false — consumers of this
// helper (read-side ACL on coach views of athlete data) must treat
// pending invitations as absent until activation completes.
export const isEdgeActive = (edge: CoachAthleteEdge | null | undefined) =>
  !!edge && edge.status === "active";

// Does this edge carry `grant`? Deliberately built ON isEdgeActive: a
// grant is meaningless without a live relationship, so a revoked edge
// that still lists grants (we keep them for audit) must not authorise
// anything. Any capability check goes through here — never read
// `edge.grants` directly at a call site, or the "must also be active"
// half of the rule will eventually be forgotten at one of them.
export const edgeHasGrant = (
  edge: CoachAthleteEdge | null | undefined,
  grant: CoachGrant,
): boolean => isEdgeActive(edge) && !!edge?.grants?.includes(grant);

// Wire shape for POST /api/me/coach-edges. Extends NewCoachAthleteEdge
// with `inviterRole` since a single user can be a coach to some peers
// and an athlete to others — the role can't be inferred from caller
// sub alone. Defaults to 'coach' since the family-app primary case is
// the coach (Antenore) inviting his athletes (the kids) to be
// coached. The symmetric "athlete inviting a coach" path stays
// reachable via inviterRole='athlete'.
export const InviteCoachAthleteEdge = NewCoachAthleteEdge.extend({
  inviterRole: z.enum(["coach", "athlete"]).default("coach"),
});
export type InviteCoachAthleteEdge = z.infer<typeof InviteCoachAthleteEdge>;
